import { mapGroupBy } from '@plato/shared-util/map';
import { keepPreviousData } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import type {
    ActivityFeedModelType,
    ActivityFeedRecord,
    InsightOutcomeResponseFilterOptions,
} from '../../../server/types/customer-activity-feed-types.ts';
import { api } from '../../../utils/api.ts';
import { useActivityFilters } from './use-activity-filters.ts';

type GroupedActivityFeedRecord = ActivityFeedRecord & { created_at: string };

type UseActivityFeedOptions = {
    customerId?: string;
    // Explicit filter overrides; take precedence over the values from URL filters.
    // Used by callers that render the feed outside the /activities page (e.g. the
    // Plato Revenue modal, which fixes the rep and the INTERESTED response).
    userIds?: string[];
    insightOutcomeResponses?: InsightOutcomeResponseFilterOptions[];
    modelTypes: ActivityFeedModelType[];
    /** Keeps the last list on screen while new filters load, so the page keeps its height. */
    keepPreviousList?: boolean;
};

export function useActivityFeed({
    customerId,
    userIds,
    insightOutcomeResponses,
    modelTypes,
    keepPreviousList,
}: UseActivityFeedOptions) {
    const { appliedFilters, pageSize } = useActivityFilters();

    const {
        modelType,
        modelTypes: filterModelTypes,
        customerIds,
        userIds: filterUserIds,
        insightOutcomeResponses: filterInsightOutcomeResponses,
        ...restFilters
    } = appliedFilters;

    // the new feed's multi-select, else the old feed's single type, else the caller's defaults
    const selectedModelTypes = filterModelTypes?.length ? filterModelTypes : undefined;
    const filteredModelTypes = selectedModelTypes ?? (modelType ? [modelType] : modelTypes);

    const { data: feedData, ...queryResult } =
        api.platoUser.query.customerActivity.getFeedList.withFilters.useInfiniteQuery(
            {
                filter: {
                    ...restFilters,
                    userIds: userIds ?? filterUserIds,
                    insightOutcomeResponses: insightOutcomeResponses ?? filterInsightOutcomeResponses,
                    customerIds: customerId ? [customerId] : customerIds,
                    modelTypes: filteredModelTypes,
                },
                page_size: pageSize,
            },
            {
                getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
                placeholderData: keepPreviousList ? keepPreviousData : undefined,
            },
        );

    const timeGroupedData = useMemo<Map<string, ActivityFeedRecord[]>>(() => {
        if (!feedData) {
            return new Map();
        }

        const data = uniqBy(
            feedData.pages.flatMap((page) => page.items),
            (item) => `${item.model_type}-${item.model_id}`,
        );

        return mapGroupBy(
            data.filter((activity): activity is GroupedActivityFeedRecord => Boolean(activity.created_at)),
            (activity) => startOfDay(new Date(activity.created_at)).toISOString(),
        );
    }, [feedData]);

    return {
        data: feedData,
        ...queryResult,
        timeGroupedData,
    };
}
